/**
 * Test sign-in rules shared by the API (which enforces them) and the web form
 * (which explains them before the user hits the API).
 *
 * @see docs/features/credentials.md (Test sign-in)
 */

const DOMAIN_LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/;

/**
 * Normalize an email domain for a provider (`@QA.Acme.com` → `qa.acme.com`),
 * or null when it is not a plain DNS name with at least two labels. No
 * wildcards: the provider vouches for exactly one domain.
 */
export function normalizeTestSignInDomain(input: string): string | null {
  const domain = input.trim().toLowerCase().replace(/^@/, '');
  if (domain.length === 0 || domain.length > 253) return null;
  const labels = domain.split('.');
  if (labels.length < 2) return null;
  return labels.every((label) => DOMAIN_LABEL.test(label)) ? domain : null;
}

/**
 * True when `email` sits directly in `domain` — `ada@qa.acme.com` is in
 * `qa.acme.com`, `ada@x.qa.acme.com` and `ada@acme.com` are not. Exact match
 * on purpose: the domain is the whole limit on who we can vouch for.
 */
export function isEmailInTestSignInDomain(email: string, domain: string): boolean {
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  if (at <= 0 || at !== normalized.indexOf('@')) return false;
  return normalized.slice(at + 1) === domain;
}

/**
 * Why a callback URL cannot be registered, or null when it can. OIDC matches
 * redirect_uri by exact string, so what is registered is exactly what the
 * app must send.
 */
export function testSignInRedirectUriError(uri: string): string | null {
  if (uri.includes('*')) return 'Wildcards are not allowed; register each callback URL exactly.';
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return 'Not an absolute URL.';
  }
  if (url.protocol !== 'https:') return 'Callback URLs must use https.';
  if (url.hash) return 'Callback URLs cannot contain a #fragment.';
  if (url.username || url.password) return 'Callback URLs cannot contain credentials.';
  return null;
}
